import React, { useMemo, useState } from 'react';
import type { RuleGroupType } from 'react-querybuilder';
import { Formik, useFormikContext } from 'formik';
import { isEmpty } from 'lodash';
import * as Yup from 'yup';

import { useStyles } from '~/assets/styles';
import AlertBanner from '~/components/core/AlertBanner';
import Button from '~/components/core/Atomic/Buttons/Button';
import ButtonsContainer from '~/components/core/Atomic/Buttons/ButtonsContainer';
import MenuItem from '~/components/core/Atomic/MenuItem';
import CancelButton from '~/components/core/Buttons/CancelButton';
import useFormikChangeListener from '~/components/core/Formik/FormikChangeListener';
import LobMultiSelectWithChipsFormik, {
  concreteSelectedLobs,
  isAllLobsSelected,
} from '~/components/core/Formik/LobMultiSelectWithChipsFormik';
import SubOrgMultiSelectWithChipsFormik, {
  isAllSubOrganizationsSelected,
} from '~/components/core/Formik/SubOrgMultiSelectWithChipsFormik';
import Heading from '~/components/core/TextComponents/Heading';
import type {
  SupportedEventsResponseSupportedEventModel,
  WorkflowRuleParams,
} from '~/server_shared/openapi/data-contracts';
import cn from '~/Utils/cn';
import { sortKeysByField } from '~/Utils/sortKeysByField';

import CardDialog from '../../../CardDialog';
import useOrganization from '../../../OrganizationContext';
import TextFieldFormik from '../../../TextFieldFormik';

import WorkflowConditionsQueryBuilderFormik from './QueryBuilder/WorkflowConditionsQueryBuilderFormik';
import WorkflowSelector from './WorkflowSelector';

// Use the proper backend interface for form values
interface FormValues {
  id: string;
  display_name: string;
  event_key: string;
  workflow_id: string;
  workflow_name: string;
  sub_organization_ids: number[];
  // String LOB keys, plus the widget's numeric "All" sentinel when every LOB is
  // selected — the same mixed shape user interaction already produces. Strip the
  // sentinel with `concreteSelectedLobs` before anything leaves the form.
  lobs: (string | number)[];
  is_all_lobs: boolean;
  // Additional properties that might be needed
  is_all_sub_orgs?: boolean;
  // Opaque RQB JSON tree — the only conditions representation for workflow
  // rules. The QB wrapper writes the tree here on every change.
  conditions_json?: RuleGroupType | null;
}

export const FIELD_IDS = {
  ID: 'id',
  DISPLAY_NAME: 'display_name',
  SUB_ORGANIZATION_IDS: 'sub_organization_ids',
  LOBS: 'lobs',
  ALL_LOBS: 'is_all_lobs',
  EVENT_KEY: 'event_key',
  WORKFLOW_ID: 'workflow_id',
  CONDITIONS_JSON: 'conditions_json',
} as const;

const createValidationSchema = (subOrganizationEnabled: boolean) => {
  return Yup.object().shape({
    [FIELD_IDS.DISPLAY_NAME]: Yup.string().required('Rule name is required'),
    [FIELD_IDS.EVENT_KEY]: Yup.string().required('Event is required'),
    [FIELD_IDS.WORKFLOW_ID]: Yup.string().required('Workflow is required'),
    [FIELD_IDS.SUB_ORGANIZATION_IDS]: subOrganizationEnabled
      ? Yup.array().min(1, 'At least one sub organization must be selected')
      : Yup.array(),
    [FIELD_IDS.LOBS]: Yup.array().when(FIELD_IDS.ALL_LOBS, {
      is: false,
      then: (schema) => schema.min(1, 'At least one LOB must be selected'),
      otherwise: (schema) => schema,
    }),
    // No conditions_json validation: a rule with no QB conditions is a
    // valid use case — it fires on every matching event (vacuous true),
    // matching the historical empty-conditions behaviour. The BE accepts
    // null too; both paths produce the same firing semantic.
  });
};

interface AddOrEditWorkflowRuleDialogProps {
  onSubmit: (values: WorkflowRuleParams) => Promise<void>;
  supportedEvents?: Record<string, SupportedEventsResponseSupportedEventModel>;
  onCancel: () => void;
  rule?: Record<string, unknown>;
}

const AddOrEditWorkflowRuleDialog: React.FC<AddOrEditWorkflowRuleDialogProps> = ({
  onSubmit,
  supportedEvents,
  onCancel,
  rule,
}) => {
  const { subOrganizationEnabled } = useOrganization() as {
    subOrganizationEnabled: boolean;
  };

  return (
    <Formik
      initialValues={{
        [FIELD_IDS.ID]: (rule?.id as string) || '',
        [FIELD_IDS.DISPLAY_NAME]: (rule?.display_name as string) ?? '',
        [FIELD_IDS.SUB_ORGANIZATION_IDS]:
          !subOrganizationEnabled || !rule
            ? []
            : rule.is_all_sub_orgs
              ? [SubOrgMultiSelectWithChipsFormik.ALL_OPTION.id]
              : (rule.sub_organization_ids as number[]),
        // An all-LOBs rule is seeded in the widget's own representation (the "All"
        // sentinel), exactly as `sub_organization_ids` is above. Seeding the server
        // shape (`lobs: [], is_all_lobs: true`) makes the widget's mount conversion
        // rewrite both fields, which the conditions-clearing listener below reads as
        // a user edit — silently discarding the stored conditions tree on every open.
        [FIELD_IDS.LOBS]: rule?.is_all_lobs
          ? [LobMultiSelectWithChipsFormik.ALL_OPTION.id]
          : ((rule?.lobs as string[]) ?? []),
        [FIELD_IDS.ALL_LOBS]: (rule?.is_all_lobs as boolean) ?? false,
        [FIELD_IDS.EVENT_KEY]: (rule?.event_key as string) ?? '',
        [FIELD_IDS.WORKFLOW_ID]: (rule?.workflow_id as string) ?? '',
        workflow_name: (rule?.workflow_name as string) ?? '',
        [FIELD_IDS.CONDITIONS_JSON]: (rule?.conditions_json as RuleGroupType | null) ?? null,
      }}
      validationSchema={createValidationSchema(subOrganizationEnabled)}
      onSubmit={async (values, formikProps) => {
        try {
          // When sub organizations are disabled, always treat as "all sub organizations"
          const isAllSubOrganizations = subOrganizationEnabled
            ? isAllSubOrganizationsSelected(values.sub_organization_ids)
            : true;

          const submitValues: WorkflowRuleParams = {
            display_name: values.display_name,
            event_key: values.event_key,
            workflow_id: values.workflow_id,
            workflow_name: values.workflow_name || '',
            is_all_sub_orgs: isAllSubOrganizations,
            sub_organization_ids: isAllSubOrganizations ? [] : values.sub_organization_ids,
            lobs: values.is_all_lobs ? [] : concreteSelectedLobs(values.lobs ?? []),
            is_all_lobs: values.is_all_lobs,
            is_enabled: true, // Default to enabled for new rules
            workflow_engine: 'n8n', // Default workflow engine
          };
          // null is accepted by the BE as "no conditions" — the rule then fires
          // on every matching event.
          (submitValues as WorkflowRuleParams & { conditions_json?: RuleGroupType | null }).conditions_json =
            values.conditions_json ?? null;
          await onSubmit(submitValues);
          onCancel();
        } catch {
          formikProps.setSubmitting(false);
        }
      }}
    >
      {() => {
        return <AddRuleInner onCancel={onCancel} supportedEvents={supportedEvents} />;
      }}
    </Formik>
  );
};

interface AddRuleInnerProps {
  supportedEvents?: Record<string, SupportedEventsResponseSupportedEventModel>;
  onCancel: () => void;
}

const AddRuleInner: React.FC<AddRuleInnerProps> = ({ supportedEvents, onCancel }) => {
  const { subOrganizationEnabled, subOrganizations, organizationId } = useOrganization() as {
    subOrganizationEnabled: boolean;
    subOrganizations: { id: number }[];
    organizationId: number;
  };
  const { isSubmitting, handleSubmit, values, setFieldValue } = useFormikContext<FormValues>();
  const [isLoadingConditions, setIsLoadingConditions] = useState(false);

  const { classes } = useStyles();

  const isDisabled = isSubmitting || isLoadingConditions;
  const subOrganizationsValue = useMemo(() => values.sub_organization_ids, [values.sub_organization_ids]);

  const isEditing = !!values.id;

  // Get available workflows for the selected event
  const eventKey = values.event_key;

  // "All (inc. future lobs)" is stored as the sentinel id 0, not as a LOB key, so it must not reach
  // the catalog as a LOB named "0". Read off the forwarded value rather than `is_all_lobs`: that flag
  // is set by a change listener one render later, and the intervening render would fire the fetch.
  // The same derivation feeds the `isAllLobs` flag the catalog request carries.
  const isAllLobs = isAllLobsSelected(values.lobs ?? []);
  const catalogLobs = isAllLobs ? [] : concreteSelectedLobs(values.lobs ?? []);

  // reset all lobs when sub org changes
  useFormikChangeListener({
    listenForKeys: [FIELD_IDS.SUB_ORGANIZATION_IDS],
    onChange: () => {
      setFieldValue(FIELD_IDS.LOBS, []);
      setFieldValue(FIELD_IDS.ALL_LOBS, false);
    },
    runOnFirstRender: false,
  });

  // reset workflow when event changes
  useFormikChangeListener({
    listenForKeys: [FIELD_IDS.EVENT_KEY],
    onChange: () => {
      setFieldValue(FIELD_IDS.WORKFLOW_ID, '');
      setFieldValue('workflow_name', '');
    },
    runOnFirstRender: false,
  });

  // Clear the persisted conditions_json tree whenever a scope dimension
  // changes. The catalog refetches on these keys (event scope filters fields;
  // LOB/sub-org narrow which providers contribute), so a tree authored under
  // the old scope would silently re-validate under the new one if its paths
  // happened to remain valid — saving the rule under a scope the user didn't
  // intend.
  useFormikChangeListener({
    listenForKeys: [FIELD_IDS.EVENT_KEY, FIELD_IDS.LOBS, FIELD_IDS.ALL_LOBS, FIELD_IDS.SUB_ORGANIZATION_IDS],
    onChange: () => {
      setFieldValue(FIELD_IDS.CONDITIONS_JSON, null);
    },
    runOnFirstRender: false,
  });

  return (
    <CardDialog
      isDialog
      onClose={onCancel}
      maxWidth="md"
      fullWidth
      title={`${isEditing ? 'Edit' : 'Add'} Workflow Rule`}
    >
      <AlertBanner
        className="mb-4"
        alertType="info"
        note="Editing the sub organizations and line of businesses will clear all conditions."
        title="Please Note"
        withIcon
      />
      <TextFieldFormik
        id={FIELD_IDS.DISPLAY_NAME}
        label="Rule Name"
        className={cn(classes.textField, 'mb-20')}
        fullWidth
        disabled={isDisabled}
      />
      <div className="rounded-md border border-solid border-slate-600 px-20 pb-20 pt-16">
        <Heading variant={Heading.TYPES.H3} className="pb-12">
          Context
        </Heading>
        {subOrganizationEnabled ? (
          <div className="mb-12">
            <SubOrgMultiSelectWithChipsFormik
              id={FIELD_IDS.SUB_ORGANIZATION_IDS}
              label="Sub Organizations"
              showOnly={isSubmitting}
              disabled={isSubmitting}
            />
          </div>
        ) : null}
        <div className="mb-4">
          <LobMultiSelectWithChipsFormik
            lobsFieldId={FIELD_IDS.LOBS}
            allLobsFieldId={FIELD_IDS.ALL_LOBS}
            {...(subOrganizationEnabled
              ? {
                  subOrganizationIds: isAllSubOrganizationsSelected(subOrganizationsValue)
                    ? subOrganizations.map((subOrg) => subOrg.id)
                    : subOrganizationsValue,
                  subOrgFieldId: FIELD_IDS.SUB_ORGANIZATION_IDS,
                  shouldResetOnSubOrgChange: true,
                  disabled: isEmpty(subOrganizationsValue),
                }
              : {})}
          />
        </div>
      </div>
      <TextFieldFormik
        id={FIELD_IDS.EVENT_KEY}
        label="Event"
        className={cn(classes.textField, 'mb-20 w-1/2')}
        fullWidth
        disabled={isSubmitting}
        select
      >
        {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
        {/* @ts-ignore */}
        {supportedEvents ? (
          sortKeysByField(supportedEvents, 'desc').map((key) => (
            <MenuItem key={key} value={key}>
              {supportedEvents[key].desc}
            </MenuItem>
          ))
        ) : (
          <MenuItem value="" disabled>
            No events available
          </MenuItem>
        )}
      </TextFieldFormik>
      <div className="rounded-md border border-solid border-slate-600 px-20 pb-20 pt-16">
        <Heading variant={Heading.TYPES.H3}>Details</Heading>
        <div className="mb-4">
          <WorkflowSelector
            organizationId={organizationId}
            eventKey={eventKey}
            id={FIELD_IDS.WORKFLOW_ID}
            label="Workflow"
            disabled={isDisabled || !eventKey}
            className={classes.textField}
          />
        </div>
      </div>
      <div className="px-4 py-20">
        <WorkflowConditionsQueryBuilderFormik
          fieldId={FIELD_IDS.CONDITIONS_JSON}
          eventKey={eventKey || ''}
          // Every picked LOB: the catalog is the cross-LOB intersection, which
          // is also what save-time validation checks the rule against.
          lobs={catalogLobs}
          isAllLobs={isAllLobs}
          isAllSubOrgs={subOrganizationEnabled ? isAllSubOrganizationsSelected(values.sub_organization_ids) : true}
          subOrgIds={subOrganizationEnabled ? values.sub_organization_ids : []}
          // Disables Save while the catalog is in flight.
          onLoadingChange={setIsLoadingConditions}
        />
      </div>

      <ButtonsContainer>
        <CancelButton onClick={onCancel} disabled={isSubmitting} />
        <Button color="primary" onClick={() => handleSubmit()} disabled={isDisabled}>
          {isEditing ? 'Update' : 'Save'}
        </Button>
      </ButtonsContainer>
    </CardDialog>
  );
};

export default AddOrEditWorkflowRuleDialog;
// AddRuleInner is exported for testing the Formik change-listener wiring
// (e.g. scope-change reset of conditions_json). Production code should use the
// default export.
export { AddRuleInner };
